// biome-ignore-all lint/plugin: 本文件是 import.meta.env 唯一合法访问点（CONVENTIONS §7.12）
import { z } from "zod";

/**
 * 配置的唯一闸门。**两层，界线是可判定的**（蓝图 ADR-0102）：
 *
 * - **编译期常量** —— 在任何环境里取值都相同的东西（产品名），外加构建自身的元数据
 *   （commit / 时间：它们描述的正是这一次构建，按定义属于产物）。
 * - **运行期配置** —— **在两个环境里取值不同的任何东西**（网关基址、认证服务器地址、
 *   OIDC client id、mock 开关）。来自 `/config.json`：生产由 nginx 从
 *   ConfigMap 挂载的文件供，开发由 `vite-runtime-config.mjs` 从仓根 `config.dev.json` 供。
 *
 * 为什么必须分开：编译期烘进去的环境事实会让**同一个 digest 在两个环境里不是同一个
 * 东西**，与 ADR-0045 ⑥ 的「digest 即真值」直接冲突，且失败点最晚 —— 页面 200、
 * 健康检查绿、冒烟绿，只在用户点登录那一刻断。
 *
 * ⚠️ 本 app 是这条裁定最尖锐的一例：迁移前 `.env.development` 与 `.env.production`
 *    **逐字相同**（都写 `http://auth.eiam.localhost`），于是 `assert-prod-build` 的
 *    「分岔集为空」判定会打绿 —— 而产物里确实烤着一个只在开发环境成立的地址。
 *    「两边取值相同」推不出「不是环境事实」；它只说明生产那一份从来没被给过真值。
 */

// ── 编译期常量 ───────────────────────────────────────────────────────────────
const buildSchema = z.object({
  VITE_PUBLIC_APP_NAME: z.string().min(1),
  VITE_PUBLIC_BUILD_COMMIT: z.string().default("dev"),
  VITE_PUBLIC_BUILD_TIME: z.string().default("dev"),
});
const parsedBuild = buildSchema.safeParse(import.meta.env);
if (!parsedBuild.success) {
  console.error(
    "[env] Invalid build-time environment variables:",
    z.treeifyError(parsedBuild.error),
  );
  throw new Error("Invalid build-time environment variables");
}
const build = parsedBuild.data;

// ── 运行期配置 ───────────────────────────────────────────────────────────────
const runtimeSchema = z.object({
  /**
   * edge-gateway 的前端出口。空串 = 同源相对路径：
   * - dev 由 vite.config.ts 的 proxy（`/api` `/kratos` `/hydra` `/keto` `/authz-core`）转发
   * - prod 由 nginx 与 edge-gateway 同 host 共享，`apiClient` 直接发相对 URL
   */
  edgeBaseUrl: z.string(),
  /** OIDC authorize 跳转用，必须是跨域完整 URL。 */
  authServerUrl: z.url(),
  /** 员工受众共用的 Hydra client（单 PEP 换 code）。 */
  oidcClientId: z.string().min(1),
  /** `true` = 挂 MSW 假后端。 */
  enableMock: z.enum(["true", "false"]).default("false"),
});
type RuntimeConfig = z.infer<typeof runtimeSchema>;

let runtime: RuntimeConfig | null = null;

/**
 * 在渲染任何东西**之前**取运行期配置，**失败即停**。
 *
 * 取不到就白屏并报错，好过带着 undefined 的地址跑起来 —— 后者把一个部署错误
 * （ConfigMap 没挂上）伪装成一个功能错误（登录点了没反应）。
 */
export async function loadRuntimeConfig(): Promise<void> {
  const res = await fetch("/config.json", { cache: "no-store" });
  if (!res.ok) {
    throw new Error(
      `运行期配置 /config.json 取不到（HTTP ${res.status}）。` +
        `生产：该文件由部署单元的 ConfigMap 挂进 nginx 的 web 根 —— 404 多半是忘了挂。` +
        `开发：由 vite-runtime-config 插件从仓根 config.dev.json 供 —— 404 多半是那个文件不在。`,
    );
  }
  runtime = runtimeSchema.parse(await res.json());
}

/** 测试用：直接注入，不经网络。生产代码永远不该调它。 */
export function __setRuntimeConfigForTests(cfg: RuntimeConfig): void {
  runtime = runtimeSchema.parse(cfg);
}

type Env = {
  readonly APP_NAME: string;
  readonly BUILD_COMMIT: string;
  readonly BUILD_TIME: string;
  readonly EDGE_BASE_URL: string;
  readonly AUTH_SERVER_URL: string;
  readonly OIDC_CLIENT_ID: string;
  readonly ENABLE_MOCK: boolean;
  readonly IS_DEV: boolean;
  readonly IS_PROD: boolean;
};

const RUNTIME_KEYS = {
  EDGE_BASE_URL: "edgeBaseUrl",
  AUTH_SERVER_URL: "authServerUrl",
  OIDC_CLIENT_ID: "oidcClientId",
} as const satisfies Record<string, keyof RuntimeConfig>;

const BUILD_KEYS = {
  APP_NAME: "VITE_PUBLIC_APP_NAME",
  BUILD_COMMIT: "VITE_PUBLIC_BUILD_COMMIT",
  BUILD_TIME: "VITE_PUBLIC_BUILD_TIME",
} as const satisfies Record<string, keyof typeof build>;

function need(prop: string): RuntimeConfig {
  if (!runtime) {
    throw new Error(
      `在 loadRuntimeConfig() 完成之前读取了 env.${prop} —— ` +
        `运行期配置必须在渲染之前加载（见 src/app/bootstrap.ts）。测试里用 __setRuntimeConfigForTests()。`,
    );
  }
  return runtime;
}

/**
 * 用 Proxy 而不是把消费点改成 async：调用点一字不改（`env.AUTH_SERVER_URL` 仍是同步的），
 * 而「还没加载就读」是一个**响亮的错误**，不是一个 undefined 悄悄流下去。
 */
export const env: Env = new Proxy({} as Env, {
  get(_target, prop: string) {
    const b = BUILD_KEYS[prop as keyof typeof BUILD_KEYS];
    if (b) return build[b];
    if (prop === "IS_DEV") return import.meta.env.DEV;
    if (prop === "IS_PROD") return import.meta.env.PROD;
    if (prop === "ENABLE_MOCK") return need(prop).enableMock === "true";
    const key = RUNTIME_KEYS[prop as keyof typeof RUNTIME_KEYS];
    if (!key) return undefined;
    return need(prop)[key];
  },
});
